import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Toggle } from "@/components/ui/toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { IGAutomationTrigger } from "@/types/IGAutomation";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface IGAutomation {
  _id: string;
  keyword?: string;
  trigger?: IGAutomationTrigger;
  includeDMs?: boolean;
  includeComments?: boolean;
  response?: string;
  emailPrompt?: string;
  mentionsCount?: number;
  enabled: boolean;
}

export function InstagramEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [automation, setAutomation] = useState<IGAutomation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [formData, setFormData] = useState({
    keyword: "",
    trigger: IGAutomationTrigger.STARTS_WITH,
    includeDMs: false,
    includeComments: true,
    response: "",
    emailPrompt: "",
    mentionsCount: undefined as number | undefined,
    enabled: false,
  });

  useEffect(() => {
    async function fetchAutomation() {
      if (!id) return;

      try {
        const response = await fetch(`/api/automations/instagram/${id}`);
        if (response.ok) {
          const data = await response.json();
          const automationData = data.automation;
          setAutomation(automationData);

          setFormData({
            keyword: automationData.keyword || "",
            trigger: automationData.trigger || IGAutomationTrigger.STARTS_WITH,
            includeDMs: automationData.includeDMs || false,
            includeComments: automationData.includeComments !== undefined ? automationData.includeComments : true,
            response: automationData.response || "",
            emailPrompt: automationData.emailPrompt || "",
            mentionsCount: automationData.mentionsCount,
            enabled: automationData.enabled || false,
          });
        } else {
          console.error("Failed to fetch automation");
        }
      } catch (error) {
        console.error("Failed to fetch automation:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAutomation();

    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  useEffect(() => {
    if (automation) {
      const keywordLabel = formData.keyword || "Untitled";
      setBreadcrumbs([
        { label: "Flows", path: "/automations" },
        { label: "Instagram", path: "/automations/instagram" },
        { label: keywordLabel, path: `/automations/instagram/${id}/edit` },
        { label: "Edit", path: `/automations/instagram/${id}/edit` },
      ]);
    }
  }, [formData.keyword, automation, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "mentionsCount") {
      const numValue = value === "" ? undefined : parseInt(value, 10);
      setFormData((prev) => ({ ...prev, [name]: isNaN(numValue as number) ? undefined : numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEnabledChange = (pressed: boolean) => {
    setFormData((prev) => ({ ...prev, enabled: pressed }));
  };

  const getTriggerValue = () => {
    if (formData.includeDMs && formData.includeComments) return "both";
    if (formData.includeDMs) return "dms";
    return "comments";
  };

  const handleTriggerChange = (value: string) => {
    if (value === "dms") {
      setFormData((prev) => ({ ...prev, includeDMs: true, includeComments: false }));
    } else if (value === "comments") {
      setFormData((prev) => ({ ...prev, includeDMs: false, includeComments: true }));
    } else if (value === "both") {
      setFormData((prev) => ({ ...prev, includeDMs: true, includeComments: true }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsSubmitting(true);
    try {
      const payload = {
        keyword: formData.keyword || undefined,
        trigger: formData.trigger,
        includeDMs: formData.includeDMs,
        includeComments: formData.includeComments,
        response: formData.response || undefined,
        emailPrompt: formData.emailPrompt || undefined,
        mentionsCount: formData.trigger === IGAutomationTrigger.MENTIONS ? formData.mentionsCount : undefined,
        enabled: formData.enabled,
      };

      const response = await fetch(`/api/automations/instagram/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate(`/automations/instagram`);
      } else {
        const data = await response.json();
        console.error("Failed to update automation:", data.error);
        alert(data.error || "Failed to update flow");
      }
    } catch (error) {
      console.error("Failed to update automation:", error);
      alert("An error occurred while updating the flow");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!automation) {
    return <div className="p-4">Flow not found</div>;
  }

  return (
    <div className="p-4">
      <Tabs value="edit" className="mb-4">
        <TabsList>
          <TabsTrigger
            value="edit"
            onClick={() => navigate(`/automations/instagram/${id}/edit`)}
          >
            Edit
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            onClick={() => navigate(`/automations/instagram/${id}/runs`)}
          >
            Runs
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Edit Instagram Flow</CardTitle>
          <Toggle
            pressed={formData.enabled}
            onPressedChange={handleEnabledChange}
            aria-label="Toggle enabled"
            className={
              formData.enabled
                ? "bg-green-500 hover:bg-green-600 text-white data-[state=on]:bg-green-500 data-[state=on]:text-white"
                : "bg-gray-500 hover:bg-gray-600 text-white data-[state=on]:bg-gray-500 data-[state=on]:text-white"
            }
          >
            {formData.enabled ? "Live" : "Draft"}
          </Toggle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="trigger-select">Trigger</Label>
              <Select
                value={getTriggerValue()}
                onValueChange={handleTriggerChange}
              >
                <SelectTrigger id="trigger-select" className="w-full">
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dms">DMs</SelectItem>
                  <SelectItem value="comments">Comments</SelectItem>
                  <SelectItem value="both">DMs and Comments</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type-select">Type</Label>
              <Select
                value={formData.trigger}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, trigger: value as IGAutomationTrigger }))
                }
              >
                <SelectTrigger id="type-select" className="w-full">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={IGAutomationTrigger.STARTS_WITH}>Starts With</SelectItem>
                  <SelectItem value={IGAutomationTrigger.INCLUDES}>Includes</SelectItem>
                  <SelectItem value={IGAutomationTrigger.MENTIONS}>Mentions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="keyword">Keyword</Label>
              <Input
                id="keyword"
                name="keyword"
                value={formData.keyword}
                onChange={handleChange}
                placeholder="Enter keyword"
              />
            </div>

            {formData.trigger === IGAutomationTrigger.MENTIONS && (
              <div className="space-y-2">
                <Label htmlFor="mentionsCount">Mentions Count</Label>
                <Input
                  id="mentionsCount"
                  name="mentionsCount"
                  type="number"
                  value={formData.mentionsCount ?? ""}
                  onChange={handleChange}
                  placeholder="Enter mentions count"
                  min="1"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="response">Response</Label>
              <Textarea
                id="response"
                name="response"
                value={formData.response}
                onChange={handleChange}
                placeholder="Enter response"
                rows={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailPrompt">Email Prompt</Label>
              <Textarea
                id="emailPrompt"
                name="emailPrompt"
                value={formData.emailPrompt}
                onChange={handleChange}
                placeholder="Enter email prompt"
                rows={6}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/automations/instagram`)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

